// src/games/HuntWords/Game/index.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Gestures } from 'react-gesture-handler';

import { ICell, IHuntWord, ISolution, LayoutProps } from '../huntWordInterface';
import { Container, Letter, Square } from './styles';

interface Block {
  layout?: LayoutProps;
  index: number;
  letter: string;
  x: string;
  y: string;
  isCorrect: boolean;
  ref: HTMLDivElement;
}

interface GameProps {
  i: number; // quantidade de colunas
  j: number; // quantidade de linhas
  /** Palavras ainda não encontradas. */
  solutions: ISolution[];
  data: IHuntWord;
  isFinished: boolean;
  resetGame: boolean;
  /** Células (índice row-major) já confirmadas no progresso salvo. */
  initialCorrectCells: number[];
  acceptResponse: (solution: ISolution, cells: number[]) => void;
  toggleResetGame: () => void;
}

// Auxiliares fora do componente: não causam renderização (como antes).
let preventDefault = -1;
let track: number[] = [];
const blockMargin = 0;
const hoverColor = '#bc6ce4'; // cor ao deslizar o dedo/mouse
const acceptedColor = 'rgb(84, 173, 72)'; // palavra encontrada

const layoutOf = (ref: HTMLDivElement): LayoutProps => ({
  height: ref.offsetHeight,
  width: ref.offsetWidth,
  x: ref.offsetLeft,
  y: ref.offsetTop,
});

const Game: React.FC<GameProps> = ({
  i: columns,
  j: lines,
  solutions,
  data,
  isFinished,
  resetGame,
  initialCorrectCells,
  acceptResponse,
  toggleResetGame,
}) => {
  const blocks = useRef<Block[]>([]);
  const solutionsRef = useRef(solutions);
  solutionsRef.current = solutions;
  const acceptRef = useRef(acceptResponse);
  acceptRef.current = acceptResponse;
  const finishedRef = useRef(isFinished);
  finishedRef.current = isFinished;

  const directionV = columns;
  const directionD1 = columns + 1;
  const directionD2 = columns - 1;

  const updateTable = () => {
    for (const block of blocks.current) {
      if (!block?.ref) continue;
      block.ref.style.background = block.isCorrect ? acceptedColor : 'transparent';
    }
  };

  useEffect(() => {
    window.onresize = () => {
      for (const block of blocks.current) {
        if (block?.ref) block.layout = layoutOf(block.ref);
      }
    };
    return () => {
      window.onresize = null;
    };
  }, []);

  // Retomada: pinta as células das palavras já encontradas.
  useEffect(() => {
    initialCorrectCells.forEach((cell) => {
      if (blocks.current[cell]) blocks.current[cell].isCorrect = true;
    });
    updateTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resetGame) return;
    blocks.current.forEach((block) => {
      if (block) block.isCorrect = false;
    });
    updateTable();
    toggleResetGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetGame]);

  const blockElement = (index: number, cell: ICell) => (
    <Square
      selecting={false}
      amountColumns={columns}
      amountLines={lines}
      ref={(ref: HTMLDivElement) => {
        if (!ref) return;
        blocks.current[index] = {
          index,
          ref,
          letter: cell.attributes.solution,
          x: cell.attributes.x,
          y: cell.attributes.y,
          isCorrect: blocks.current[index]?.isCorrect ?? false,
          layout: layoutOf(ref),
        };
      }}
      key={index}
    >
      <Letter>{cell.attributes.solution}</Letter>
    </Square>
  );

  // Grade criada uma vez (como antes), para não refazer as células a cada render.
  const [grid] = useState(() =>
    Array.from({ length: columns * lines }, (_, index) => blockElement(index, data.grid.cell[index]))
  );

  const newDirection = (from: number, to: number, step: number) => {
    updateTable();
    track = [];
    if (from < to) {
      for (let k = from; k <= to; k += step) {
        blocks.current[k].ref.style.background = hoverColor;
        track.push(k);
      }
    } else {
      for (let k = from; k >= to; k -= step) {
        blocks.current[k].ref.style.background = hoverColor;
        track.push(k);
      }
    }
  };

  const tracker = (x: number, y: number) => {
    if (finishedRef.current) return;
    for (const block of blocks.current) {
      if (!block?.layout) break;
      const inside =
        x >= block.layout.x &&
        x <= block.layout.x + block.layout.width &&
        y >= block.layout.y &&
        y <= block.layout.y + block.layout.height;
      if (!inside) continue;

      if (preventDefault === block.index) break;
      preventDefault = block.index;

      if (track.length === 0) {
        block.ref.style.background = hoverColor;
        track.push(block.index);
        break;
      }

      const initial = blocks.current[track[0]];
      if (!initial.layout) break;
      const dx = Math.round((block.layout.x - initial.layout.x) / (block.layout.width + blockMargin * 2));
      const dy = Math.round((block.layout.y - initial.layout.y) / (block.layout.height + blockMargin * 2));

      if (initial.index + dx === block.index) newDirection(initial.index, block.index, 1);
      if (initial.index + directionV * dy === block.index) newDirection(initial.index, block.index, directionV);
      if (initial.index + directionD1 * dy === block.index) newDirection(initial.index, block.index, directionD1);
      if (initial.index + directionD2 * dy === block.index) newDirection(initial.index, block.index, directionD2);
    }
  };

  const validate = () => {
    if (!finishedRef.current && track.length > 0) {
      const first = blocks.current[track[0]];
      const last = blocks.current[track[track.length - 1]];
      const match = solutionsRef.current.find(
        (s) =>
          (s.x[0] === first.x && s.x[1] === last.x && s.y[0] === first.y && s.y[1] === last.y) ||
          (s.x[1] === first.x && s.x[0] === last.x && s.y[1] === first.y && s.y[0] === last.y)
      );
      if (match) {
        const cells = [...track];
        cells.forEach((cell) => {
          blocks.current[cell].isCorrect = true;
        });
        acceptRef.current(match, cells);
      }
    }
    preventDefault = -1;
    updateTable();
    track = [];
  };

  const handleGesture = (event: HammerInput) => {
    tracker(event.center.x, event.center.y);
  };

  return (
    <Gestures
      recognizers={{
        Pan: {
          events: {
            pan: handleGesture,
            panend: validate,
          },
        },
      }}
    >
      <Container>{grid}</Container>
    </Gestures>
  );
};

export default Game;
